// ============================================================
// useCombat - Combat orchestration hook (composition layer).
//
// Composes useApplyHits, useAttackExecution, and
// useDefenceResolution sub-hooks. computeCountdown delegates
// to the pure engine function.
// ============================================================
import type { AveragedDamageMode } from '../engine/survival/averagedDamage';
import { DEFAULT_STACK_POLICY, type StackPolicy } from '../engine/stackPolicy';
import { useState } from 'react';
import { computeCountdownPure, shouldRunPerAttackCountdown } from '../engine/index';
import useApplyHits from './useApplyHits';
import useAttackExecution from './useAttackExecution';
import useDefenceResolution from './useDefenceResolution';
import type { UseAttackExecutionParams, PendingReroll, PendingBonusAttack } from './useAttackExecution';
import type { UseDefenceResolutionParams, UseDefenceResolutionReturn } from './useDefenceResolution';
import type { Tab, TurnTrackerState } from '../types';
import type { ConditionModOptions } from '../engine/conditionModifiers';

/**
 * useCombat - Full attack orchestration: ammo check, skill roll, jam check,
 * hit generation with force fields, damage calc with all weapon specials,
 * attack re-roll (Blademaster / Sure Strike), defence prompt or direct
 * applyHits, and post-hit effects processing.
 */
export interface UseCombatParams extends Omit<
  UseAttackExecutionParams,
  | 'pendingReroll'
  | 'setPendingReroll'
  | 'pendingBonusAttack'
  | 'setPendingBonusAttack'
  | 'computeCountdown'
  | 'applyHits'
> {
  /** @deprecated unused - kept for call-site compatibility */
  syncTargetToFirestore?: unknown;
  clearPendingDefence?: UseDefenceResolutionParams['clearPendingDefence'];
  mp?: UseDefenceResolutionParams['mp'];
  /** Condition-modifier house rule - passed through to defence resolution/forecasts. */
  applyConditionModifiers?: boolean;
  /** Which condition modifiers the table has switched off. */
  condModOptions?: ConditionModOptions;
  /** The acting combatant's conditions + immunity traits, for to-hit penalties (v7.0.0). */
  actorConditionContext?: { conditions: string[] } & Record<string, unknown>;
  /** A reaction was taken by this tab's combatant - RAW spends their aim (v8.217.0). */
  onReactionTaken?: UseDefenceResolutionParams['onReactionTaken'];
  /** Turn tracker (for countdown dual-mode) */
  turnTrackerState?: TurnTrackerState | null;
  /** GM house rule: timed conditions count down each round. Default false. */
  roundCountdown?: boolean;
  /** What a condition does to one already worn (v8.367.0). Default extend. */
  stackPolicy?: StackPolicy;
  /** The table's Protagonist ruling. Absent means off. */
  protagonistRules?: boolean;
  /** The table's averaged-damage ruling. Absent means off. */
  averagedDamage?: AveragedDamageMode;
  /** Squad Mode: a victim took heavy fire (see useApplyHits). */
  onCohesionHit?: (tabId: string, weapon: string) => void;
}

export interface UseCombatReturn extends UseDefenceResolutionReturn {
  handleAttack: () => void;
  handleRerollChoice: (accept: boolean) => void;
  pendingReroll: PendingReroll | null;
  pendingBonusAttack: PendingBonusAttack | null;
  setPendingBonusAttack: (pba: PendingBonusAttack | null) => void;
  handleBonusAttackChoice: (accept: boolean) => void;
  computeCountdown: UseAttackExecutionParams['computeCountdown'];
}

/** What the per-attack countdown reads besides the tab. */
interface CountdownRules {
  isDefencePending: UseCombatParams['isDefencePending'];
  turnTrackerState: TurnTrackerState | null | undefined;
  roundCountdown: boolean;
  protagonistRules: boolean;
}

/**
 * Look up the tab and delegate to the pure engine function. Skip the
 * countdown during defence, to avoid modifying a frozen target, and for
 * tracked participants, whose countdown runs at turn start.
 */
function countdownFor(tabs: Tab[], tabId: string, rules: CountdownRules) {
  if (rules.isDefencePending && rules.isDefencePending(tabId)) return null;
  const tab = tabs.find((t) => t.id === tabId);
  if (!tab) return null;
  if (!shouldRunPerAttackCountdown(tab.target, rules.turnTrackerState ?? null, tabId, rules.roundCountdown))
    return null;
  const tName = tab.target.name || tab.label || '';
  return computeCountdownPure(tab.target, tName, rules.roundCountdown, rules.protagonistRules);
}

/** The defence chain, wired to the shared pending attack and this combat's applyHits. */
function useDefence(
  params: UseCombatParams,
  applyHits: UseDefenceResolutionParams['applyHits'],
  setPendingBonusAttack: (pba: PendingBonusAttack | null) => void,
) {
  return useDefenceResolution({
    pendingAttack: params.pendingAttack as UseDefenceResolutionParams['pendingAttack'],
    setPendingAttack: params.setPendingAttack as UseDefenceResolutionParams['setPendingAttack'],
    tabs: params.tabs,
    applyHits,
    // Phase 7: Furious Assault after defence
    setPendingBonusAttack: setPendingBonusAttack as UseDefenceResolutionParams['setPendingBonusAttack'],
    // Multiplayer defence
    sessionId: params.sessionId,
    playerId: params.playerId,
    clearPendingDefence: params.clearPendingDefence,
    mp: params.mp,
    unnaturalDosMode: params.unnaturalDosMode,
    applyConditionModifiers: params.applyConditionModifiers,
    condModOptions: params.condModOptions,
    onReactionTaken: params.onReactionTaken,
  });
}

/** The table's rulings this combat reads, defaulted: all off, and the default stack policy. */
function combatRules({
  roundCountdown = false,
  stackPolicy = DEFAULT_STACK_POLICY,
  protagonistRules = false,
}: UseCombatParams) {
  return { roundCountdown, stackPolicy, protagonistRules };
}

/** applyHits for this combat - the post-hit pipeline under the table's rulings. */
function useCombatApply(params: UseCombatParams, rules: ReturnType<typeof combatRules>) {
  const { tabs, syncNarrativeToFirestore, updateTab, addNarrative, historyTrack, onCohesionHit } = params;
  const deps = { tabs, syncNarrativeToFirestore, updateTab, addNarrative, historyTrack, onCohesionHit };
  return useApplyHits({ ...deps, ...rules }).applyHits;
}

export default function useCombat(params: UseCombatParams): UseCombatReturn {
  const rules = combatRules(params);
  // Phase 6: Pending re-roll state (co-located with pendingAttack)
  const [pendingReroll, setPendingReroll] = useState<PendingReroll | null>(null);
  // Phase 7: Pending bonus attack state (Furious Assault)
  const [pendingBonusAttack, setPendingBonusAttack] = useState<PendingBonusAttack | null>(null);

  const countdownRules = {
    ...rules,
    isDefencePending: params.isDefencePending,
    turnTrackerState: params.turnTrackerState,
  };
  const computeCountdown: UseAttackExecutionParams['computeCountdown'] = (tabId) =>
    countdownFor(params.tabs, tabId, countdownRules);
  const applyHits = useCombatApply(params, rules);
  const attack = useAttackExecution({
    ...params,
    pendingReroll,
    setPendingReroll,
    pendingBonusAttack,
    setPendingBonusAttack,
    computeCountdown,
    applyHits,
  });
  const defence = useDefence(params, applyHits as UseDefenceResolutionParams['applyHits'], setPendingBonusAttack);

  return { ...attack, pendingReroll, pendingBonusAttack, setPendingBonusAttack, ...defence, computeCountdown };
}
